import { Injectable } from '@angular/core';

import { getAccessTokenPayloadInfo } from 'projects/lib-shared-common/src/public-api';
import { externalCommonAspURL, externalNetcoreURL } from 'projects/lib-shared-common/src/lib/functions/external-url-redirect';
import { StaticApplicationId } from 'projects/lib-shared-core/src/lib/Constants/static-application-id.enum';

export type SvcSourceLinkType = 'asp' | 'netcore';

export interface SvcParsedSourceLink {
  type: SvcSourceLinkType;
  url: string;
  paramsObj?: Record<string, string>;
  paramsStr?: string;
  applicationId?: string;
  redirectId?: number;
  additionalRedirectId?: number;
}

type AspRedirectParams = Parameters<typeof externalCommonAspURL>[1];

const SOURCE_LINK_PATTERN = /^(redirectdynamicpageasp|redirectdynamicpage|externalcommonaspurl)\s*\(\s*['"]([^'"]+)['"]\s*,\s*(.+)\)\s*$/i;

/** Function names whose source link targets a legacy ASP page. */
const ASP_FN_NAMES = ['redirectdynamicpageasp', 'externalcommonaspurl'];

@Injectable({ providedIn: 'root' })
export class SvcSourceLinkRedirectService {
  public parse(sourceLink?: string | null): SvcParsedSourceLink | null {
    if (!sourceLink) return null;

    const trimmed = sourceLink.trim().replace(/;$/, '');
    const match = SOURCE_LINK_PATTERN.exec(trimmed);
    if (!match) return null;

    const [, fnName, url, rawParams] = match;
    const type: SvcSourceLinkType = ASP_FN_NAMES.includes(fnName.toLowerCase()) ? 'asp' : 'netcore';
    const paramsContent = rawParams?.trim()?.replace(/\)\s*$/, '').trim();

    if (type === 'asp') {
      const paramsStr = paramsContent?.replace(/^['"]|['"]$/g, '').replace(/^{|}$/g, '');
      const paramsObj = paramsStr ? this.#buildAspParams(paramsStr) : {};
      const redirectId = paramsObj['redirectId'] ? Number(paramsObj['redirectId']) : undefined;
      return { type, url, paramsObj, applicationId: paramsObj['system'], redirectId };
    }

    const paramsStr = paramsContent?.replace(/^['"]|['"]$/g, '');
    const redirectId = this.#matchId(paramsStr, /redirectId\s*[:=]\s*(\d+)/i);
    const additionalRedirectId = this.#matchId(paramsStr, /additionalRedirectId\s*[:=]\s*(\d+)/i);

    if (url?.toLowerCase()?.includes('digitalobeya')) {
      const obeyaUrl = `${this.#resolveObeyaOrigin(url)}/digitalobeya/Board`;
      const redirectPart = `redirectId=${redirectId ?? 0}`;
      const additionalPart = additionalRedirectId ? `|additionalRedirectId=${additionalRedirectId}` : '';
      const obeyaParams = `{controller=Room||action=RoomRedirect||variables={${redirectPart}${additionalPart}}}`;
      return { type, url: obeyaUrl, paramsStr: obeyaParams, applicationId: StaticApplicationId.digitalObeya, redirectId, additionalRedirectId };
    }

    return { type, url, paramsStr, redirectId, additionalRedirectId };
  }

  public openSourceLink(sourceLink?: string | null): boolean {
    const parsed = this.parse(sourceLink);
    if (!parsed?.url) return false;

    if (parsed.type === 'asp') {
      const params: Record<string, string> = { ...(parsed.paramsObj ?? {}) };
      if (parsed.applicationId?.toUpperCase() === StaticApplicationId.projects.toUpperCase()) {
        params['system'] = StaticApplicationId.projects;
      }
      externalCommonAspURL(parsed.url, params as AspRedirectParams);
      return true;
    }

    if (parsed.type === 'netcore' && parsed.paramsStr) {
      externalNetcoreURL(parsed.url, parsed.paramsStr);
      return true;
    }

    return false;
  }

  #buildAspParams(paramsStr: string): Record<string, string> {
    return paramsStr.split(',').reduce((acc, param) => {
      const divider = param.includes(':') ? ':' : '=';
      const [rawKey, ...rawValue] = param.split(divider);
      const key = rawKey?.trim();
      if (key) acc[key] = rawValue.join(divider).trim().replace(/^['"]|['"]$/g, '');
      return acc;
    }, {} as Record<string, string>);
  }

  #matchId(paramsStr: string | undefined, pattern: RegExp): number | undefined {
    const found = pattern.exec(paramsStr ?? '');
    return found ? Number(found[1]) : undefined;
  }

  #resolveObeyaOrigin(url: string): string {
    const issuerOrigin = this.#safeOrigin(getAccessTokenPayloadInfo('iss'));
    if (issuerOrigin) return issuerOrigin;
    return this.#safeOrigin(url) ?? url.split('/').slice(0, 3).join('/');
  }

  #safeOrigin(value?: string | null): string | undefined {
    if (!value) return undefined;
    try {
      return new URL(value).origin;
    } catch {
      return undefined;
    }
  }
}
